import React, { useState, useEffect } from 'react';
import api from '../../api';
import AppliedCandidatesModule from '../AppliedCandidatesModule';
import { X, MessageSquare } from 'lucide-react';
import '../../pages/FacultyDashboard.css';

const AppliedCandidatesContainer = ({ initialCompany = null, initialRole = null, onBack = null }) => {
  const [appliedCandidates, setAppliedCandidates] = useState([]);
  const [loadingApplied, setLoadingApplied] = useState(false);
  const [selectedAppliedCompany, setSelectedAppliedCompany] = useState(initialCompany);
  const [selectedAppliedRole, setSelectedAppliedRole] = useState(initialRole);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [applicationsViewMode, setApplicationsViewMode] = useState(initialCompany ? 'grouped' : 'grouped');
  
  const [candidateSearch, setCandidateSearch] = useState('');
  const [candidateStatusFilter, setCandidateStatusFilter] = useState('All');
  const [candidateCompanyFilter, setCandidateCompanyFilter] = useState('All');
  const [candidateRoleFilter, setCandidateRoleFilter] = useState('All');
  
  const [remarksModalOpen, setRemarksModalOpen] = useState(false);
  const [remarksAppId, setRemarksAppId] = useState(null);
  const [remarksText, setRemarksText] = useState('');
  const [savingRemarks, setSavingRemarks] = useState(false);

  useEffect(() => {
    fetchAppliedCandidates();
  }, []);

  const fetchAppliedCandidates = async () => {
    setLoadingApplied(true);
    try {
      const res = await api.get('/tpo/applied-candidates');
      const formattedCandidates = (res.data || []).map(candidate => ({
        ...candidate,
        company_name: candidate.company_name ? candidate.company_name.replace(/\b\w/g, l => l.toUpperCase()) : candidate.company_name
      }));
      setAppliedCandidates(formattedCandidates);
    } catch (err) {
      console.error("Error fetching applied candidates", err);
    } finally {
      setLoadingApplied(false);
    }
  };

  const handleUpdateAppStatus = async (appId, newStatus) => {
    try {
      await api.put(`/tpo/applied-candidates/${appId}/status`, { status: newStatus });
      fetchAppliedCandidates();
      if (selectedApplication && selectedApplication.id === appId) {
        setSelectedApplication({ ...selectedApplication, status: newStatus });
      }
    } catch (err) {
      console.error("Error updating application status", err);
    }
  };

  const openRemarksModal = (app) => {
    setRemarksAppId(app.id);
    setRemarksText(app.remarks || '');
    setRemarksModalOpen(true);
  };

  const saveRemarks = async () => {
    if (!remarksAppId || !remarksText.trim()) return;
    setSavingRemarks(true);
    try {
      await api.put(`/tpo/applied-candidates/${remarksAppId}/status`, { 
        status: 'REMARKED',
        remarks: remarksText.trim()
      });
      fetchAppliedCandidates();
      if (selectedApplication && selectedApplication.id === remarksAppId) {
        setSelectedApplication({ ...selectedApplication, status: 'REMARKED', remarks: remarksText.trim() });
      }
      setRemarksModalOpen(false);
      setRemarksAppId(null);
      setRemarksText('');
    } catch (err) {
      console.error("Error saving remarks", err);
    } finally {
      setSavingRemarks(false);
    }
  };

  const getStatusStyle = (statusStr) => {
    const s = (statusStr || 'APPLIED').toUpperCase();
    if (s === 'APPROVED') {
      return { background: 'rgba(16, 185, 129, 0.15)', color: '#059669', border: '1px solid rgba(16, 185, 129, 0.4)' };
    }
    if (s === 'UNDER REVIEW' || s === 'UNDER_REVIEW') {
      return { background: 'rgba(245, 158, 11, 0.15)', color: '#d97706', border: '1px solid rgba(245, 158, 11, 0.4)' };
    }
    if (s === 'REMARKED') {
      return { background: 'rgba(249, 115, 22, 0.15)', color: '#ea580c', border: '1px solid rgba(249, 115, 22, 0.4)' };
    }
    if (s === 'PENDING') {
      return { background: 'rgba(234, 179, 8, 0.15)', color: '#ca8a04', border: '1px solid rgba(234, 179, 8, 0.4)' };
    }
    if (s === 'REJECTED') {
      return { background: 'rgba(239, 68, 68, 0.15)', color: '#dc2626', border: '1px solid rgba(239, 68, 68, 0.4)' };
    }
    return { background: 'var(--faculty-bg)', color: 'var(--faculty-muted)', border: '1px solid var(--faculty-border)' };
  };

  return (
    <div className="faculty-dashboard" style={{ position: 'relative', width: '100%' }}>
      <AppliedCandidatesModule
        appliedCandidates={appliedCandidates}
        selectedApplication={selectedApplication}
        setSelectedApplication={setSelectedApplication}
        selectedAppliedCompany={selectedAppliedCompany}
        setSelectedAppliedCompany={setSelectedAppliedCompany}
        selectedAppliedRole={selectedAppliedRole}
        setSelectedAppliedRole={setSelectedAppliedRole}
        loadingApplied={loadingApplied}
        applicationsViewMode={applicationsViewMode}
        setApplicationsViewMode={setApplicationsViewMode}
        candidateSearch={candidateSearch}
        setCandidateSearch={setCandidateSearch}
        candidateCompanyFilter={candidateCompanyFilter}
        setCandidateCompanyFilter={setCandidateCompanyFilter}
        candidateRoleFilter={candidateRoleFilter}
        setCandidateRoleFilter={setCandidateRoleFilter}
        candidateStatusFilter={candidateStatusFilter}
        setCandidateStatusFilter={setCandidateStatusFilter}
        handleUpdateAppStatus={handleUpdateAppStatus}
        openRemarksModal={openRemarksModal}
        getStatusStyle={getStatusStyle}
        onBackToDrives={onBack}
      />

      {remarksModalOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.55)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          padding: '20px'
        }}>
          <div style={{
            background: 'var(--faculty-bg, #ffffff)',
            borderRadius: '16px',
            width: '100%',
            maxWidth: '520px',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.25)',
            border: '1px solid var(--faculty-border, #e5e7eb)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <div style={{
              padding: '20px 24px',
              borderBottom: '1px solid var(--faculty-border, #e5e7eb)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              background: 'linear-gradient(to right, #eff6ff, #ffffff)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <MessageSquare size={22} style={{ color: '#2563eb' }} />
                <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700, color: 'var(--faculty-text, #111827)' }}>
                  Add Remarks
                </h3>
              </div>
              <button
                onClick={() => setRemarksModalOpen(false)}
                style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', padding: 4 }}
              >
                <X size={20} />
              </button>
            </div>
            <div style={{ padding: '24px' }}>
              <p style={{ margin: '0 0 16px 0', fontSize: '0.9rem', color: 'var(--faculty-muted, #4b5563)', lineHeight: 1.5 }}>
                Leave a note for the candidate regarding their application status. This can be viewed later.
              </p>
              <textarea
                value={remarksText}
                onChange={(e) => setRemarksText(e.target.value)}
                placeholder="E.g., Missing updated resume, Need to verify GitHub links, Selected for next round..."
                style={{
                  width: '100%',
                  minHeight: '140px',
                  padding: '16px',
                  borderRadius: '12px',
                  border: '1px solid var(--faculty-border, #d1d5db)',
                  background: 'var(--faculty-bg, #f9fafb)',
                  color: 'var(--faculty-text, #111827)',
                  fontSize: '0.95rem',
                  resize: 'vertical',
                  outline: 'none',
                  boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.02)'
                }}
              />
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px' }}>
                <button
                  onClick={() => setRemarksModalOpen(false)}
                  style={{
                    padding: '10px 20px',
                    borderRadius: '8px',
                    border: '1px solid var(--faculty-border, #d1d5db)',
                    background: 'var(--faculty-bg, #ffffff)',
                    color: 'var(--faculty-text, #374151)',
                    cursor: 'pointer',
                    fontWeight: 600,
                    fontSize: '0.95rem'
                  }}
                  disabled={savingRemarks}
                >
                  Cancel
                </button>
                <button
                  onClick={saveRemarks}
                  disabled={savingRemarks || !remarksText.trim()}
                  style={{
                    padding: '10px 24px',
                    borderRadius: '8px',
                    border: 'none',
                    background: '#2563eb',
                    color: '#ffffff',
                    cursor: savingRemarks || !remarksText.trim() ? 'not-allowed' : 'pointer',
                    fontWeight: 600,
                    fontSize: '0.95rem',
                    opacity: savingRemarks || !remarksText.trim() ? 0.6 : 1,
                    boxShadow: '0 4px 12px rgba(37, 99, 235, 0.2)'
                  }}
                >
                  {savingRemarks ? 'Saving...' : 'Save Remarks'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppliedCandidatesContainer;
